// IMPORT REACT
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef
} from 'react'

// IMPORT UI COMPONENTS
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography
} from '@mui/material'

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import {
  GetFullPrescriptionReportDetail,
  GetFullPrescriptionTokenDetail,
} from '../../services/TokenService';
import {
  useLocation,
} from 'react-router-dom';
import { PreferencesContext } from '../../PreferenceContext';
import { AlertType } from '../../services/models/AlertType';
import PriscriptionFooter from './Priscription/PriscriptionFooter';
import PriscriptionHeader from './Priscription/PriscriptionHeader';
import PatientDetails from './Priscription/PatientDetails';
import PriscriptionContent from './Priscription/PriscriptionContent';
import { format } from 'date-fns';
import { PAGE_SIZE } from '../../services/common/const';
import HeightChart from '../../components/Chart/HeightChart';
import WeightChart from '../../components/Chart/WeightChart';
import BMIChart from '../../components/Chart/BMIChart';
import HeadCircumferenceChart from '../../components/Chart/HeadCircumferenceChart';
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

interface Parameter {
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  startDate: Date;
  endDate: Date;
}
const parameters: Parameter = {
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  startDate: new Date(),
  endDate: new Date(),
};
interface BranchDetails {
  consumerId: number;
  organizationId: number;
  branchId: number;
  opdId: number;
  departmentId: number;
  specialityId: number;
  patientId: number;
  description: string;
  dateOfBirth: Date | null;
}

const FullPrescriptionReport = () => {
  const initialBranchDetails: BranchDetails = {
    consumerId: 0,
    organizationId: 0,
    branchId: 0,
    opdId: 0,
    departmentId: 0,
    specialityId: 0,
    patientId: 0,
    description: "",
    dateOfBirth: null,
  };

  const [open, setOpen] = React.useState(true);
  const [zoomLevel, setZoomLevel] = React.useState(1);
  const [branchDetails, setBranchDetails] = React.useState<BranchDetails>(initialBranchDetails);
  const [detailedOpd, setDetailedOpd] = React.useState(null);
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [param, setParam] = React.useState(parameters);

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isDownload, setIsDownload] = React.useState(false);

  const zoomOptions = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  const htmlprintRef = useRef<HTMLTableElement>(null);
  const heightChartRef = useRef<HTMLDivElement>(null);
  const weightChartRef = useRef<HTMLDivElement>(null);
  const bmiChartRef = useRef<HTMLDivElement>(null);
  const headChartRef = useRef<HTMLDivElement>(null);
  const certificateRef = useRef<HTMLDivElement>(null);

  // Getting Seach Paramms for Pagination
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let token = urlParams.get("t");
  let currentToken: string = '';

  if (token !== null && token !== undefined) {
    currentToken = token;
  }

  //CLOSE FOR DIALOG
  const handleClose = (event: any, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    setOpen(false);
  };

  const handleZoomChange = (event: any) => {
    setZoomLevel(parseFloat(event.target.value));
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => {
      const nextZoom = zoomOptions.find((z) => z > prev) || prev;
      return nextZoom;
    });
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => {
      const nextZoom =
        [...zoomOptions].reverse().find((z) => z < prev) || prev;
      return nextZoom;
    });
  };

  const handleReportDownload = async () => {
    setIsDownload(true);
    await new Promise(r => setTimeout(r, 100));

    const mainContent = htmlprintRef.current;
    if (!mainContent) {
      setIsDownload(false);
      return;
    }

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 10;
    const contentWidth = pageWidth - 2 * margin;

    // Capture header, patient, footer elements once
    let headerImgData = null;
    let headerImgHeight = 0;
    let patientImgData = null;
    let patientImgHeight = 0;
    let footerImgData = null;
    let footerImgHeight = 0;

    // Capture header
    const headerElement = document.querySelector('.header');
    if (headerElement && detailedOpd?.businessInformation?.headerEnable) {
      const headerCanvas = await html2canvas(headerElement as HTMLElement, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      headerImgData = headerCanvas.toDataURL('image/png', 1.0);
      headerImgHeight = (headerCanvas.height * contentWidth) / headerCanvas.width;
    }

    // Capture patient details
    const patientElement = document.querySelector('.patient-details-section');
    if (patientElement) {
      const patientCanvas = await html2canvas(patientElement as HTMLElement, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      patientImgData = patientCanvas.toDataURL('image/png', 1.0);
      patientImgHeight = (patientCanvas.height * contentWidth) / patientCanvas.width;
    }

    // Capture footer
    const footerElement = document.querySelector('.footer');
    if (footerElement) {
      const footerCanvas = await html2canvas(footerElement as HTMLElement, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      footerImgData = footerCanvas.toDataURL('image/png', 1.0);
      footerImgHeight = (footerCanvas.height * contentWidth) / footerCanvas.width;
    }

    // Helper function to add header, patient, footer to page
    const addHeaderPatientFooter = () => {
      let yPos = margin;

      // Add header
      if (headerImgData) {
        pdf.addImage(headerImgData, 'PNG', margin, yPos, contentWidth, headerImgHeight);
        yPos += headerImgHeight;

        // Add horizontal line between header and patient details
        pdf.setDrawColor(221, 221, 221); // #ddd color
        pdf.setLineWidth(0.5);
        pdf.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 2; // Add spacing after the line
      }

      // Add patient details
      if (patientImgData) {
        pdf.addImage(patientImgData, 'PNG', margin, yPos, contentWidth, patientImgHeight);
        yPos += patientImgHeight;
      }

      // Add footer at bottom
      if (footerImgData) {
        const footerYPos = pageHeight - margin - footerImgHeight;
        pdf.addImage(footerImgData, 'PNG', margin, footerYPos, contentWidth, footerImgHeight);
      }

      return yPos;
    };

    // Capture main content
    const contentElement = document.querySelector('.content');
    const mainCanvas = await html2canvas(contentElement as HTMLElement, {
      scale: 2,
      useCORS: true,
      logging: false
    });
    const mainImgData = mainCanvas.toDataURL('image/png', 1.0);
    const mainImgHeight = (mainCanvas.height * contentWidth) / mainCanvas.width;

    // Calculate available content area per page
    const lineHeight = 2; // Height for the divider line and spacing
    const topSectionHeight = margin + headerImgHeight + lineHeight + patientImgHeight;
    const bottomSectionHeight = margin + footerImgHeight;
    const availableContentHeight = pageHeight - topSectionHeight - bottomSectionHeight;

    let remainingHeight = mainImgHeight;
    let sourceY = 0;
    let isFirstPage = true;

    // Loop through content pages
    while (remainingHeight > 0) {
      if (!isFirstPage) {
        pdf.addPage();
      }

      // Add header, patient, footer
      const contentStartY = addHeaderPatientFooter();

      const heightToRender = Math.min(remainingHeight, availableContentHeight);

      // Create a temporary canvas for the slice
      const sliceCanvas = document.createElement('canvas');
      const sliceCtx = sliceCanvas.getContext('2d');

      sliceCanvas.width = mainCanvas.width;
      sliceCanvas.height = (heightToRender / mainImgHeight) * mainCanvas.height;

      if (sliceCtx) {
        sliceCtx.drawImage(
          mainCanvas,
          0,
          (sourceY / mainImgHeight) * mainCanvas.height,
          mainCanvas.width,
          sliceCanvas.height,
          0,
          0,
          sliceCanvas.width,
          sliceCanvas.height
        );
      }

      const sliceImgData = sliceCanvas.toDataURL('image/png', 1.0);
      pdf.addImage(sliceImgData, 'PNG', margin, contentStartY, contentWidth, heightToRender);

      remainingHeight -= availableContentHeight;
      sourceY += availableContentHeight;
      isFirstPage = false;
    }

    // Add certificate
    if (certificateRef.current) {
      pdf.addPage();
      const contentStartY = addHeaderPatientFooter();

      const certCanvas = await html2canvas(certificateRef.current, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      const certImgData = certCanvas.toDataURL('image/png', 1.0);
      const certImgHeight = (certCanvas.height * contentWidth) / certCanvas.width;

      pdf.addImage(certImgData, 'PNG', margin, contentStartY, contentWidth, certImgHeight);
    }

    // Add charts
    const charts = [
      { ref: heightChartRef, name: 'Height Chart' },
      { ref: weightChartRef, name: 'Weight Chart' },
      { ref: bmiChartRef, name: 'BMI Chart' },
      { ref: headChartRef, name: 'Head Circumference Chart' }
    ];

    for (const chart of charts) {
      if (chart.ref.current) {
        pdf.addPage();
        const contentStartY = addHeaderPatientFooter();

        const chartCanvas = await html2canvas(chart.ref.current, {
          scale: 2,
          useCORS: true,
          logging: false
        });
        const chartImgData = chartCanvas.toDataURL('image/png', 1.0);
        const chartImgHeight = (chartCanvas.height * contentWidth) / chartCanvas.width;

        pdf.addImage(chartImgData, 'PNG', margin, contentStartY, contentWidth, chartImgHeight);
      }
    }

    pdf.save('prescription-report.pdf');
    setIsDownload(false);
  };

  const getFullPrescriptionTokenDetail = async () => {
    try {
      let tokenData: any = null;
      const params = new URLSearchParams();
      params.append("token", token || "");

      const result: any = await GetFullPrescriptionTokenDetail(params, currentToken);

      if (result?.data?.data) {
        const data = result.data.data;

        tokenData = {
          consumerId: data.consumerId ?? 0,
          organizationId: data.organizationId ?? 0,
          branchId: data.branchId ?? 0,
          opdId: data.opdId ?? 0,
          departmentId: data.departmentId ?? 0,
          specialityId: data.specialityId ?? 0,
          patientId: data.patientId ?? 0,
          dateOfBirth: data.dateOfBirth || null,
        };

        setBranchDetails(tokenData);
      }
      return tokenData;
    } catch (e) {
      console.error("Error fetching branch details:", e);
    }
  };

  const getFullPrescriptionReportDetail = async (
    opdId: string,
    consumerId: string,
    organizationId: string,
    branchId: string,
    departmentId: string,
    specialityId: string,
    patientId: string,
    dateOfBirth: Date | null,
  ) => {
    try {
      let params = new URLSearchParams();
      let reportData = null;
      params.append("opdId", opdId);
      params.append("consumerId", consumerId);
      params.append("branchId", branchId);
      params.append("organizationId", organizationId);
      params.append("departmentId", departmentId);
      params.append("specialityId", specialityId);
      params.append("patientId", patientId);
      if (dateOfBirth) {
        params.append("startDate", format(dateOfBirth, "yyyy-MM-dd"));
      }
      if (param?.endDate) {
        params.append("endDate", format(param.endDate, "yyyy-MM-dd"));
      }
      const result: any = await GetFullPrescriptionReportDetail(params, currentToken);
      if (result?.data?.data) {
        reportData = result?.data?.data;
        setDetailedOpd(reportData);
      }
      return reportData;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      const tokenData = await getFullPrescriptionTokenDetail();
      if (tokenData?.opdId || 0) {
        await getFullPrescriptionReportDetail(
          tokenData.opdId || 0,
          tokenData.consumerId || 0,
          tokenData.organizationId || 0,
          tokenData.branchId || 0,
          tokenData.departmentId || 0,
          tokenData.specialityId || 0,
          tokenData.patientId || 0,
          tokenData.dateOfBirth || null
        );
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USE EFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, []);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: "4px",
          height: "100vh",
          padding: "0px",
          margin: "0px",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #ddd",
          padding: "4px 16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={0}
            sx={{ padding: 0, margin: 0 }}
          >
            <Select
              value={zoomLevel}
              onChange={handleZoomChange}
              sx={{
                mx: 1,
                minWidth: 70,
                border: "none",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                backgroundColor: "transparent",
                padding: 0,
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1px solid #c7c5c5",
                    borderRadius: "4px",
                  },
                },
              }}
            >
              {zoomOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <Typography
                    sx={{
                      fontSize: "13px !important",
                    }}
                  >
                    {(option * 100).toFixed(0)}%
                  </Typography>
                </MenuItem>
              ))}
            </Select>
            <Box display="flex" gap={1}>
              <Tooltip title="Zoom Out" placement="top">
                <IconButton
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 0.25}
                  sx={{ padding: 0 }}
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Zoom In" placement="top">
                <IconButton
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 2}
                  sx={{ padding: 0 }}
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Tooltip title="Download PDF" placement="top">
              <IconButton
                aria-label="Download PDF"
                onClick={handleReportDownload}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          height: "100%",
          padding: "16px !important",
          fontFamily: "Arial, sans-serif",
          backgroundColor: "#f1f3f5",
          boxSizing: "border-box",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            width: "max-content",
            border: "1px solid #ddd",
            boxShadow: "4px rgba(0, 0, 0, 0.2)",
            borderRadius: "2px",
            padding: "30px 50px",
            fontFamily: "Arial, sans-serif",
            backgroundColor: "white",
            boxSizing: "border-box",
            transform: `scale(${zoomLevel})`,
            transformOrigin: "center",
            transition: "transform 0.2s ease-in-out",
            margin: "0 auto",
          }}
        >
          {/* UI DISPLAY */}
          {!isDownload && (
            <Box sx={{ width: "794px" }}>
              <table style={{ width: '100%', pageBreakInside: 'avoid' }}>
                <thead>
                  <tr>
                    <td>
                      {detailedOpd?.businessInformation?.headerEnable && (
                        <Box className="header">
                          <PriscriptionHeader headerData={detailedOpd?.businessInformation} />
                        </Box>
                      )}
                      <Box className="patient-details-section">
                        <PatientDetails patientData={detailedOpd} />
                      </Box>
                    </td>
                  </tr>
                </thead>
                <tbody style={{ height: '100%', verticalAlign: 'top' }}>
                  <tr style={{ height: '100%' }}>
                    <td>
                      <Box className="content">
                        <PriscriptionContent
                          opdContent={detailedOpd}
                          selectedSections={{
                            VITALS_AND_ANTHROPOMETRY: true,
                            CHIEF_COMPLAINTS: true,
                            HISTORY: true,
                            EXAMINATION: true,
                            VACCINATION: true,
                            INVESTIGATION: true,
                            DIAGNOSIS: true,
                            PRESCRIPTION: true,
                            FOLLOWUP: true,
                            DOCTOR_TO_BE_REFERRED: true
                          }}
                        />
                        {detailedOpd?.reportCertificateColl?.length > 0 && (
                          <Box mt={3}>
                            <Divider sx={{ marginBottom: '24px', mx: -6.5 }}/>
                            {detailedOpd?.reportCertificateColl?.map((cert, index) => (
                              <React.Fragment key={cert.reportCertificateId}>
                                {index > 0 && (
                                  <Divider
                                    sx={{
                                      my: 2,
                                      borderColor: '#c7c5c5',
                                      borderBottomWidth: '1.5px'
                                    }}
                                  />
                                )}
                                {/* {cert.reportCertificateName && (
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: 600,
                                      margin: '12px 16px 6px',
                                      textTransform: 'uppercase'
                                    }}
                                  >
                                    {cert.reportCertificateName}
                                  </Typography>
                                )} */}
                                <Box
                                  className="tiptap-editor-preview"
                                  sx={{
                                    margin: '0 16px 10px',
                                    lineHeight: 1.5,
                                    "& p, & li, & td, & th, & div, & span": {
                                      lineHeight: "1.5 !important",
                                    },
                                    "& table": {
                                      borderCollapse: "collapse",
                                      tableLayout: "fixed",
                                      width: "100% !important",
                                      minWidth: "100% !important",
                                      maxWidth: "100% !important",
                                      margin: "0 auto",
                                    }
                                  }}
                                  dangerouslySetInnerHTML={{ __html: cert.description }}
                                />
                              </React.Fragment>
                            ))}
                          </Box>
                        )}
                        {(() => {
                          const growthSeries = detailedOpd?.growthChartSeriesColl ?? [];
                          const hasHeight = growthSeries.some(item => item?.height > 0);
                          const hasWeight = growthSeries.some(item => item?.weight > 0);
                          const hasBMI = growthSeries.some(item => item?.bmi > 0);
                          const hasHead = growthSeries.some(item => item?.headCircumference > 0);

                          return (
                            <Box mt={3}>
                              <Divider sx={{ marginBottom: '24px', mx: -6.5}} />
                              {hasHeight && (
                                <Box sx={{ marginBottom: '40px' }}>
                                  <HeightChart
                                    motherHeight={detailedOpd?.growthChartSeriesColl?.[0]?.motherHeight ?? 0}
                                    fatherHeight={detailedOpd?.growthChartSeriesColl?.[0]?.fatherHeight ?? 0}
                                    heightList={detailedOpd?.growthChartSeriesColl}
                                    intervalType={detailedOpd?.growthChartSeriesColl?.[0]?.intervalType ?? 'YEAR'}
                                    heightChartZoneData={detailedOpd?.heightChartZoneColl}
                                    isMobileScreen={false}
                                  />
                                </Box>
                              )}
                              {hasWeight && (
                                <Box sx={{ marginBottom: '40px' }}>
                                  <WeightChart
                                    weightList={detailedOpd?.growthChartSeriesColl}
                                    intervalType={detailedOpd?.growthChartSeriesColl?.[0]?.intervalType ?? 'YEAR'}
                                    weightChartZoneData={detailedOpd?.weightChartZoneColl}
                                    isMobileScreen={false}
                                  />
                                </Box>
                              )}
                              {hasBMI && (
                                <Box sx={{ marginBottom: '40px' }}>
                                  <BMIChart
                                    bmiList={detailedOpd?.growthChartSeriesColl}
                                    intervalType={detailedOpd?.growthChartSeriesColl?.[0]?.intervalType ?? 'YEAR'}
                                    bmiChartZoneData={detailedOpd?.bmiChartZoneColl}
                                    isMobileScreen={false}
                                  />
                                </Box>
                              )}
                              {hasHead && (
                                <Box>
                                  <HeadCircumferenceChart
                                    headCircumferenceList={detailedOpd?.growthChartSeriesColl}
                                    intervalType={detailedOpd?.growthChartSeriesColl?.[0]?.intervalType ?? 'YEAR'}
                                    headCircumferenceChartZoneData={detailedOpd?.hcChartZoneColl}
                                    isMobileScreen={false}
                                  />
                                </Box>
                              )}
                            </Box>
                          );
                        })()}
                        <Box className="footer" sx={{ marginTop: '20px' }}>
                          <PriscriptionFooter footerData={detailedOpd} selectedSections={{ FOOTER: true }} />
                        </Box>
                      </Box>
                    </td>
                  </tr>
                </tbody>
              </table>
            </Box>
          )}

          {/* PDF CONTENT */}
          {isDownload && (
            <Box ref={htmlprintRef} sx={{ width: "794px" }}>
              <table style={{ width: '100%', pageBreakInside: 'avoid' }}>
                <thead>
                  <tr>
                    <td>
                      {detailedOpd?.businessInformation?.headerEnable && (
                        <Box className="header">
                          <PriscriptionHeader headerData={detailedOpd?.businessInformation} />
                        </Box>
                      )}
                      <Box className="patient-details-section">
                        <PatientDetails patientData={detailedOpd} />
                      </Box>
                    </td>
                  </tr>
                </thead>
                <tbody style={{ height: '100%', verticalAlign: 'top' }}>
                  <tr style={{ height: '100%' }}>
                    <td>
                      <Box className="content">
                        <PriscriptionContent
                          opdContent={detailedOpd}
                          selectedSections={{
                            VITALS_AND_ANTHROPOMETRY: true,
                            CHIEF_COMPLAINTS: true,
                            HISTORY: true,
                            EXAMINATION: true,
                            VACCINATION: true,
                            INVESTIGATION: true,
                            DIAGNOSIS: true,
                            PRESCRIPTION: true,
                            FOLLOWUP: true,
                            DOCTOR_TO_BE_REFERRED: true
                          }}
                        />
                      </Box>
                    </td>
                  </tr>
                </tbody>
              </table>
            </Box>
          )}

          <Box sx={{ position: 'absolute', left: '-9999px', top: 0 }}>
            {(() => {
              const growthSeries = detailedOpd?.growthChartSeriesColl ?? [];
              const hasHeight = growthSeries.some(item => item?.height > 0);
              const hasWeight = growthSeries.some(item => item?.weight > 0);
              const hasBMI = growthSeries.some(item => item?.bmi > 0);
              const hasHead = growthSeries.some(item => item?.headCircumference > 0);

              return (
                <>
                  {hasHeight && (
                    <Box ref={heightChartRef} sx={{ width: '794px', marginBottom: '20px' }}>
                      <HeightChart
                        motherHeight={detailedOpd?.growthChartSeriesColl?.[0]?.motherHeight ?? 0}
                        fatherHeight={detailedOpd?.growthChartSeriesColl?.[0]?.fatherHeight ?? 0}
                        heightList={detailedOpd?.growthChartSeriesColl}
                        intervalType={detailedOpd?.growthChartSeriesColl?.[0]?.intervalType ?? 'YEAR'}
                        heightChartZoneData={detailedOpd?.heightChartZoneColl}
                        isMobileScreen={false}
                        isDownload={true}
                      />
                    </Box>
                  )}
                  {hasWeight && (
                    <Box ref={weightChartRef} sx={{ width: '794px', marginBottom: '20px' }}>
                      <WeightChart
                        weightList={detailedOpd?.growthChartSeriesColl}
                        intervalType={detailedOpd?.growthChartSeriesColl?.[0]?.intervalType ?? 'YEAR'}
                        weightChartZoneData={detailedOpd?.weightChartZoneColl}
                        isMobileScreen={false}
                        isDownload={true}
                      />
                    </Box>
                  )}
                  {hasBMI && (
                    <Box ref={bmiChartRef} sx={{ width: '794px', marginBottom: '20px' }}>
                      <BMIChart
                        bmiList={detailedOpd?.growthChartSeriesColl}
                        intervalType={detailedOpd?.growthChartSeriesColl?.[0]?.intervalType ?? 'YEAR'}
                        bmiChartZoneData={detailedOpd?.bmiChartZoneColl}
                        isMobileScreen={false}
                        isDownload={true}
                      />
                    </Box>
                  )}
                  {hasHead && (
                    <Box ref={headChartRef} sx={{ width: '794px' }}>
                      <HeadCircumferenceChart
                        headCircumferenceList={detailedOpd?.growthChartSeriesColl}
                        intervalType={detailedOpd?.growthChartSeriesColl?.[0]?.intervalType ?? 'YEAR'}
                        headCircumferenceChartZoneData={detailedOpd?.hcChartZoneColl}
                        isMobileScreen={false}
                        isDownload={true}
                      />
                    </Box>
                  )}
                </>
              );
            })()}
            {detailedOpd?.reportCertificateColl?.length > 0 && (
              <Box ref={certificateRef} sx={{ width: '794px' }}>
                {detailedOpd?.reportCertificateColl?.map((cert, index) => (
                  <React.Fragment key={cert.reportCertificateId}>
                    {index > 0 && (
                      <Divider
                        sx={{
                          my: 2,
                          borderColor: '#c7c5c5',
                          borderBottomWidth: '1.5px'
                        }}
                      />
                    )}
                    {/* {cert.reportCertificateName && (
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          margin: '12px 16px 6px',
                          textTransform: 'uppercase'
                        }}
                      >
                        {cert.reportCertificateName}
                      </Typography>
                    )} */}
                    <Box
                      className="tiptap-editor-preview"
                      sx={{
                        margin: '0 16px 10px',
                        lineHeight: 1.5,
                        "& p, & li, & td, & th, & div, & span": {
                          lineHeight: "1.5 !important",
                        },
                        "& table": {
                          borderCollapse: "collapse",
                          tableLayout: "fixed",
                          width: "100% !important",
                          minWidth: "100% !important",
                          maxWidth: "100% !important",
                          margin: "0 auto",
                        }
                       }}
                      dangerouslySetInnerHTML={{ __html: cert.description }}
                    />
                  </React.Fragment>
                ))}
              </Box>
            )}
            <Box className="footer" sx={{ marginTop: '20px' }}>
              <PriscriptionFooter footerData={detailedOpd} selectedSections={{ FOOTER: true }} />
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default FullPrescriptionReport
